/**
 * Matching: a mentor for a student, asked from the portal and drawn as a rope
 * (Sep 17 2026, Thomas's third partner ask).
 *
 * Students on the left, the institution's mentors on the right, both from
 * Enrollment by first name and last initial. An administrator pulls a rope
 * from a student to a mentor (a drag, or a click on each) and confirms; the
 * request goes to the platform through p3-match-gateway as team@, lands
 * PENDING, and the mentor accepts or declines in the app. The student is not
 * asked, which is what P3's own staff do from Retool today.
 *
 * Every rope is a request this institution sent here, with the status the
 * platform last gave it: a dashed rope is waiting on the mentor, a solid one
 * is a mentorship, a faint one has ended. The state is read from the platform
 * on the hourly tick and never assumed. A mentorship that began any other way
 * (the student asked, or P3's own matching) is not a rope: it shows on the
 * student's row as "Has a mentor", the way Enrollment shows it.
 *
 * Ids only cross to the Worker; the names on the board are the roster's.
 * Under 760px the two columns stack, the ropes give way, and the list of
 * requests under the board is the record.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  Input,
  SectionHead,
  Select,
  StateCard,
} from "~/app/components/primitives";
import { ADMIN_SECTIONS } from "~/app/lib/admin-nav";
import {
  getCohorts,
  getMatches,
  getRoster,
  requestMatch,
  suggestMentors,
  withdrawMatch,
  type CohortsPayload,
  type MatchRequestWire,
  type MatchStatusWire,
  type MatchesPayload,
  type RosterPayload,
} from "~/app/lib/api";
import { DEFAULT_FILTERS, demoClaimedCount, deriveCohort, scenarioFromSearch } from "~/app/lib/demo-data";
import { formatCount, formatDate } from "~/app/lib/format";
import { liveView } from "~/app/lib/live-view";
import { ROSTER_HISTORIC, ROSTER_ROLE_NOTE, sampleCohorts, sampleRoster } from "~/app/pages/AdminRoster";
import { viewerOf, type SessionPayload } from "~/app/lib/session";

export const MATCHING_LEDE = "Pull a rope from a student to a mentor to ask for a mentorship. The mentor accepts in the app.";

/** Under the rope board. Every clause is a rule the Worker or the gateway keeps. */
export const MATCHING_NOTE =
  "A request goes to the mentor in the P3 app, who accepts or declines it there; the student is not asked. Ropes show what your team asked for here and what the mentor decided, as of the last hourly check.";

export const STATUS_LABEL: Record<MatchStatusWire, string> = {
  PENDING: "Confirming match",
  ACTIVE: "Mentoring",
  INACTIVE: "Ended",
};

/** An INACTIVE row an administrator took back (Sep 18 2026) reads as such, not as ended. */
export const WITHDRAWN_LABEL = "Withdrawn";

/**
 * The Withdraw button stays hidden on both hosts until the Worker's
 * MATCH_WITHDRAW is on (lib/env.ts says why): a sample that offers what the
 * live portal refuses would promise something P3 does not do yet.
 */
const WITHDRAW_LIVE = false;

export const statusLabel = (r: Pick<MatchRequestWire, "status" | "withdrawnAt">): string =>
  r.status === "INACTIVE" && r.withdrawnAt !== null ? WITHDRAWN_LABEL : STATUS_LABEL[r.status];

type Student = RosterPayload["students"][number];
type Mentor = RosterPayload["mentors"][number];

type RosterState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly roster: RosterPayload }
  | { readonly kind: "forbidden" }
  | { readonly kind: "down"; readonly message: string };

type MatchesState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly matches: MatchesPayload }
  | { readonly kind: "down"; readonly message: string };

/** Sample requests for /demo: one of each state, on the sample roster's people. */
export function sampleMatches(now: number, roster: RosterPayload): MatchesPayload {
  const day = 86_400;
  const s = (i: number) => roster.students[i]!.id;
  const m = (i: number) => roster.mentors[i]!.id;
  return {
    available: true,
    reason: null,
    perHour: 60,
    requests: [
      { id: "sample-r1", studentId: s(1), mentorId: m(0), status: "ACTIVE", requestedAt: now - 20 * day, checkedAt: now - 1_800, withdrawnAt: null },
      { id: "sample-r2", studentId: s(7), mentorId: m(2), status: "PENDING", requestedAt: now - 2 * day, checkedAt: now - 1_800, withdrawnAt: null },
      { id: "sample-r3", studentId: s(9), mentorId: m(1), status: "INACTIVE", requestedAt: now - 40 * day, checkedAt: now - 1_800, withdrawnAt: null },
    ],
    // Two the sample's own students made in the app, which nobody asked for
    // here, plus the one this team asked for and the mentor accepted. The
    // third is the point of the card: it is drawn, and it cannot be asked for.
    existing: [
      { studentId: s(1), mentorId: m(0), status: "ACTIVE", origin: "ADMIN_MANUAL", asked: true },
      { studentId: s(3), mentorId: m(1), status: "ACTIVE", origin: "MENTEE_REQUEST", asked: false },
      { studentId: s(5), mentorId: m(2), status: "PENDING", origin: "MENTEE_REQUEST", asked: false },
    ],
  };
}

function mentorFull(m: Mentor): boolean {
  return m.capacity !== null && m.mentorships >= m.capacity;
}

function mentorLine(m: Mentor): string {
  const count = m.capacity === null ? `${formatCount(m.mentorships)} mentoring` : `${formatCount(m.mentorships)} of ${formatCount(m.capacity)} mentoring`;
  return m.title ? `${m.title} · ${count}` : count;
}

function studentChip(s: Student) {
  if (s.mentor === "matched") return <Chip tone="good">Has a mentor</Chip>;
  if (s.mentor === "waiting") return <Chip tone="warn">Waiting</Chip>;
  return <Chip tone="neutral">No mentor</Chip>;
}

export function AdminMatching({
  basePath,
  demo = false,
  session,
}: {
  basePath: "/admin" | "/demo";
  demo?: boolean;
  session?: SessionPayload;
}) {
  const live = useLiveTenant(session);
  const scenario = useMemo(
    () => scenarioFromSearch(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
  const sampleView = useMemo(() => deriveCohort(scenario, DEFAULT_FILTERS), [scenario]);
  /** The sample dashboard's claimed count, which the board's list is a part of. */
  const sampleClaimed = useMemo(() => demoClaimedCount(scenario), [scenario]);
  const data = live.state.kind === "ready" ? live.state.data : null;
  const view = useMemo(() => (data ? liveView(data) : sampleView), [data, sampleView]);
  const isLive = data !== null && session !== undefined;

  const [roster, setRoster] = useState<RosterState>(() => ({ kind: "loading" }));
  const [matches, setMatches] = useState<MatchesState>(() => ({ kind: "loading" }));
  const [cohorts, setCohorts] = useState<CohortsPayload | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState("");
  const [cohortFilter, setCohortFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState<"all" | "without">("all");
  const [selected, setSelected] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The line under the confirm bar. It carries a TONE since Sep 17 2026: the
   * platform's refusals ("That mentor already mentors this student.", "That
   * mentor is at capacity.") arrived in the same muted grey as "Request
   * sent.", and on the first live rehearsal a refusal was read as a success.
   * A refusal is the thing the reader most needs to notice here, because
   * nothing else on the screen changes when one arrives.
   */
  const [notice, setNotice] = useState<{ text: string; tone: "ok" | "error" } | null>(null);
  const [suggested, setSuggested] = useState<Record<string, readonly string[]>>({});
  /** A withdrawal's refusal, shown in its own row beside the button that
   *  asked (Sep 19 2026, review: it showed at the top of the board). */
  const [withdrawRefusal, setWithdrawRefusal] = useState<{ requestId: string; text: string } | null>(null);
  /** The request whose Withdraw is in flight (Sep 18 2026), so its own button reads so. */
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  // Withdrawing is an administrator's act like asking: the Worker's route is
  // admin and up, so a viewer gets no button (a viewer never reaches the
  // board anyway: Enrollment is closed to them). The sample has no session
  // and acts locally, like send() does.
  const role = viewerOf(session)?.role;
  const canWithdraw = WITHDRAW_LIVE && (!isLive || role === "admin" || role === "owner");

  useEffect(() => {
    if (!session) {
      const now = Math.floor(Date.now() / 1000);
      const sample = sampleRoster(now);
      setRoster({ kind: "ready", roster: sample });
      setMatches({ kind: "ready", matches: sampleMatches(now, sample) });
      setCohorts(sampleCohorts(now, sample));
      return;
    }
    let cancelled = false;
    setRoster({ kind: "loading" });
    setMatches({ kind: "loading" });
    void getRoster().then((result) => {
      if (cancelled) return;
      if (result.ok) setRoster({ kind: "ready", roster: result.value });
      else if (result.status === 403) setRoster({ kind: "forbidden" });
      else setRoster({ kind: "down", message: result.error.message });
    });
    void getMatches().then((result) => {
      if (cancelled) return;
      if (result.ok) setMatches({ kind: "ready", matches: result.value });
      else setMatches({ kind: "down", message: result.error.message });
    });
    void getCohorts().then((result) => {
      if (!cancelled && result.ok) setCohorts(result.value);
    });
    return () => {
      cancelled = true;
    };
  }, [session, attempt]);

  const reloadMatches = useCallback(async () => {
    const result = await getMatches();
    if (result.ok) setMatches({ kind: "ready", matches: result.value });
    else setNotice({ text: result.error.message, tone: "error" });
  }, []);

  // The platform's suggestions for the selected student, once per student.
  useEffect(() => {
    if (!selected || suggested[selected] !== undefined) return;
    if (!isLive) {
      // The sample: the first two mentors not yet asked, in order.
      const asked = new Set(
        (matches.kind === "ready" ? matches.matches.requests : [])
          .filter((r) => r.studentId === selected && r.status !== "INACTIVE")
          .map((r) => r.mentorId),
      );
      const ids = roster.kind === "ready" ? roster.roster.mentors.filter((m) => !asked.has(m.id)).slice(0, 2).map((m) => m.id) : [];
      setSuggested((s) => ({ ...s, [selected]: ids }));
      return;
    }
    if (matches.kind === "ready" && !matches.matches.available) return;
    let cancelled = false;
    void suggestMentors(selected).then((result) => {
      if (cancelled) return;
      setSuggested((s) => ({ ...s, [selected]: result.ok ? result.value : [] }));
    });
    return () => {
      cancelled = true;
    };
  }, [selected, suggested, isLive, matches, roster]);

  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }

  const { institution, seat } = view;
  const commands: PaletteCommand[] = [
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
  ];

  const send = async (studentId: string, mentorId: string, studentName: string, mentorName: string) => {
    setBusy(true);
    setNotice(null);
    if (!isLive) {
      setMatches((current) =>
        current.kind === "ready"
          ? {
              kind: "ready",
              matches: {
                ...current.matches,
                requests: [
                  { id: `sample-r${Date.now()}`, studentId, mentorId, status: "PENDING", requestedAt: Math.floor(Date.now() / 1000), checkedAt: Math.floor(Date.now() / 1000), withdrawnAt: null },
                  ...current.matches.requests.filter((r) => !(r.studentId === studentId && r.mentorId === mentorId)),
                ],
              },
            }
          : current,
      );
      setNotice({ text: `Request sent. ${mentorName} would see it in the app; in the sample nothing is sent.`, tone: "ok" });
    } else {
      const result = await requestMatch(studentId, mentorId);
      if (result.ok) {
        await reloadMatches();
        setNotice({ text: `Request sent. ${mentorName} will see it in the app and decide there.`, tone: "ok" });
      } else {
        setNotice({ text: result.error.message, tone: "error" });
      }
    }
    setSelected(null);
    setTarget(null);
    setBusy(false);
    void studentName;
  };

  /**
   * Take back a request the mentor has not answered (Sep 18 2026). Live, the
   * Worker asks the gateway to delete the platform's connection and the row
   * comes back INACTIVE and dated withdrawn; the list is reloaded so the
   * chip, the rope and the pair's "already asked" mark all read from one
   * answer. The sample moves the row itself and sends nothing.
   */
  const withdraw = async (request: MatchRequestWire, mentorName: string) => {
    setBusy(true);
    setWithdrawing(request.id);
    setNotice(null);
    setWithdrawRefusal(null);
    if (!isLive) {
      const t = Math.floor(Date.now() / 1000);
      setMatches((current) =>
        current.kind === "ready"
          ? {
              kind: "ready",
              matches: {
                ...current.matches,
                requests: current.matches.requests.map((r) => (r.id === request.id ? { ...r, status: "INACTIVE", checkedAt: t, withdrawnAt: t } : r)),
                existing: current.matches.existing.filter((p) => !(p.studentId === request.studentId && p.mentorId === request.mentorId)),
              },
            }
          : current,
      );
      setNotice({ text: `Request withdrawn. ${mentorName} would no longer see it in the app; in the sample nothing is sent.`, tone: "ok" });
    } else {
      const result = await withdrawMatch(request.id);
      if (result.ok) {
        await reloadMatches();
        setNotice({ text: `Request withdrawn. ${mentorName} no longer sees it in the app.`, tone: "ok" });
      } else {
        // A withdrawal the mentor raced has ended the request on the Worker
        // too, so the list is read again and the row shows it ended.
        if (result.error.code === "RACED") await reloadMatches();
        setWithdrawRefusal({ requestId: request.id, text: result.error.message });
      }
    }
    setWithdrawing(null);
    setBusy(false);
  };

  return (
    <AdminShell
      institution={institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="matching"
      isSample={!isLive}
      sections={[...ADMIN_SECTIONS]}
      seat={seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <section className="e-sec" style={{ marginTop: 0 }}>
        <SectionHead level="page" id="matching" title="Matching" lede={MATCHING_LEDE} />


        {roster.kind === "loading" || matches.kind === "loading" ? (
          <StateCard variant="loading">Fetching enrollment and requests.</StateCard>
        ) : roster.kind === "forbidden" ? (
          <StateCard variant="empty" title={ROSTER_ROLE_NOTE.title}>
            {ROSTER_ROLE_NOTE.body}
          </StateCard>
        ) : roster.kind === "down" || matches.kind === "down" ? (
          <StateCard
            variant="down"
            title="We could not load matching"
            action={
              <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            }
          >
            {roster.kind === "down" ? roster.message : matches.kind === "down" ? matches.message : ""} The rest of the dashboard is unaffected.
          </StateCard>
        ) : !roster.roster.available ? (
          <StateCard variant="empty" title={ROSTER_HISTORIC.title}>
            {ROSTER_HISTORIC.body}
          </StateCard>
        ) : (
          <Board
            roster={roster.roster}
            sampleOf={!isLive && sampleClaimed > roster.roster.students.length ? sampleClaimed : null}
            matches={matches.matches}
            cohorts={cohorts}
            query={query}
            onQuery={setQuery}
            cohortFilter={cohortFilter}
            onCohortFilter={setCohortFilter}
            studentFilter={studentFilter}
            onStudentFilter={setStudentFilter}
            selected={selected}
            onSelect={(id) => {
              setSelected(id);
              setTarget(null);
              setNotice(null);
            }}
            target={target}
            onTarget={setTarget}
            suggested={selected ? (suggested[selected] ?? []) : []}
            busy={busy}
            notice={notice}
            onSend={send}
            canWithdraw={canWithdraw}
            withdrawRefusal={withdrawRefusal}
            withdrawing={withdrawing}
            onWithdraw={withdraw}
          />
        )}
      </section>
    </AdminShell>
  );
}

/* ==========================================================================
   The board
   ========================================================================== */

interface Point {
  readonly x: number;
  readonly y: number;
}

interface Rope {
  readonly key: string;
  readonly from: Point;
  readonly to: Point;
  readonly status: MatchStatusWire | "drawing" | "existing";
  readonly title: string;
}

function ropePath(from: Point, to: Point): string {
  const dx = Math.max(24, (to.x - from.x) / 2);
  return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
}

function Board({
  roster,
  sampleOf = null,
  matches,
  cohorts,
  query,
  onQuery,
  cohortFilter,
  onCohortFilter,
  studentFilter,
  onStudentFilter,
  selected,
  onSelect,
  target,
  onTarget,
  suggested,
  busy,
  notice,
  onSend,
  canWithdraw,
  withdrawRefusal = null,
  withdrawing,
  onWithdraw,
}: {
  roster: RosterPayload;
  /** On the sample (Sep 19 2026): the institution's claimed count, since the
   *  board lists eighteen of its students; the subtitle says "18 of 140". */
  sampleOf?: number | null;
  matches: MatchesPayload;
  cohorts: CohortsPayload | null;
  query: string;
  onQuery: (value: string) => void;
  cohortFilter: string;
  onCohortFilter: (value: string) => void;
  studentFilter: "all" | "without";
  onStudentFilter: (value: "all" | "without") => void;
  selected: string | null;
  onSelect: (id: string | null) => void;
  target: string | null;
  onTarget: (id: string | null) => void;
  suggested: readonly string[];
  busy: boolean;
  notice: { text: string; tone: "ok" | "error" } | null;
  onSend: (studentId: string, mentorId: string, studentName: string, mentorName: string) => Promise<void>;
  /** Admin and owner, and the sample. A viewer sees the list without the button. */
  canWithdraw: boolean;
  /** A withdrawal's refusal, drawn in the row it belongs to. */
  withdrawRefusal?: { requestId: string; text: string } | null;
  /** The request whose withdrawal is in flight, if any. */
  withdrawing: string | null;
  onWithdraw: (request: MatchRequestWire, mentorName: string) => Promise<void>;
}) {
  const boardRef = useRef<HTMLDivElement | null>(null);
  const rows = useRef(new Map<string, HTMLElement>());
  const [ropes, setRopes] = useState<Rope[]>([]);
  const [drawing, setDrawing] = useState<{ studentId: string; at: Point } | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const memberships = cohorts?.memberships ?? {};
  const activeCohorts = (cohorts?.cohorts ?? []).filter((c) => c.archivedAt === null);
  const students = roster.students.filter(
    (s) =>
      (needle === "" || s.name.toLowerCase().includes(needle)) &&
      (cohortFilter === "" || memberships[s.id] === cohortFilter) &&
      (studentFilter === "all" || s.mentor !== "matched"),
  );
  const mentors = roster.mentors;
  const studentName = (id: string) => roster.students.find((s) => s.id === id)?.name ?? "A student";
  const mentorName = (id: string) => roster.mentors.find((m) => m.id === id)?.name ?? "A mentor";
  const requestsFor = (studentId: string, mentorId: string) =>
    matches.requests.find((r) => r.studentId === studentId && r.mentorId === mentorId) ?? null;
  const pendingFrom = (studentId: string) => matches.requests.filter((r) => r.studentId === studentId && r.status === "PENDING").length;
  const pendingTo = (mentorId: string) => matches.requests.filter((r) => r.mentorId === mentorId && r.status === "PENDING").length;
  /** The open mentorship this pair already has on the platform, if any. */
  const pairedWith = (studentId: string, mentorId: string): "PENDING" | "ACTIVE" | null =>
    matches.existing.find((p) => p.studentId === studentId && p.mentorId === mentorId)?.status ?? null;

  const setRow = (id: string) => (el: HTMLElement | null) => {
    if (el) rows.current.set(id, el);
    else rows.current.delete(id);
  };

  const measure = useCallback((): void => {
    const board = boardRef.current;
    if (!board) return;
    const origin = board.getBoundingClientRect();
    const anchor = (id: string, side: "right" | "left"): Point | null => {
      const el = rows.current.get(id);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: (side === "right" ? r.right : r.left) - origin.left, y: r.top + r.height / 2 - origin.top };
    };
    const next: Rope[] = [];
    // The mentorships that already exist, first, so a rope this team asked
    // for is drawn over the one the platform holds for the same pair. A pair
    // this portal asked for is skipped here: it has a rope of its own below,
    // with the status the platform last gave it.
    for (const pair of matches.existing) {
      if (pair.asked) continue;
      const from = anchor(`s:${pair.studentId}`, "right");
      const to = anchor(`m:${pair.mentorId}`, "left");
      if (!from || !to) continue;
      next.push({
        key: `e:${pair.studentId}:${pair.mentorId}`,
        from,
        to,
        status: "existing",
        title: `${studentName(pair.studentId)} and ${mentorName(pair.mentorId)}: ${
          pair.status === "ACTIVE" ? "already mentoring" : "already asked in the app"
        }, not asked for here`,
      });
    }
    for (const r of matches.requests) {
      const from = anchor(`s:${r.studentId}`, "right");
      const to = anchor(`m:${r.mentorId}`, "left");
      if (!from || !to) continue;
      next.push({
        key: r.id,
        from,
        to,
        status: r.status,
        title: `${studentName(r.studentId)} and ${mentorName(r.mentorId)}: ${statusLabel(r)}, asked ${formatDate(r.requestedAt * 1000)}`,
      });
    }
    if (selected && target && !drawing) {
      const from = anchor(`s:${selected}`, "right");
      const to = anchor(`m:${target}`, "left");
      if (from && to) next.push({ key: "proposed", from, to, status: "drawing", title: "Proposed" });
    }
    if (drawing) {
      const from = anchor(`s:${drawing.studentId}`, "right");
      if (from) next.push({ key: "drawing", from, to: drawing.at, status: "drawing", title: "Pulling" });
    }
    setRopes(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches.requests, matches.existing, selected, target, drawing, students.length, mentors.length]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || !boardRef.current) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(boardRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  // The rope follows the pointer from a student's knot; released over a
  // mentor it becomes the proposal, released anywhere else it drops.
  const startDrag = (studentId: string) => (event: React.PointerEvent) => {
    if (busy) return;
    const board = boardRef.current;
    if (!board) return;
    event.preventDefault();
    onSelect(studentId);
    const origin = board.getBoundingClientRect();
    setDrawing({ studentId, at: { x: event.clientX - origin.left, y: event.clientY - origin.top } });
    const move = (e: PointerEvent) => {
      const o = board.getBoundingClientRect();
      setDrawing({ studentId, at: { x: e.clientX - o.left, y: e.clientY - o.top } });
      const under = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>("[data-mentor-id]");
      setHovered(under?.dataset["mentorId"] ?? null);
    };
    const up = (e: PointerEvent) => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      setDrawing(null);
      setHovered(null);
      const under = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>("[data-mentor-id]");
      const mentorId = under?.dataset["mentorId"];
      if (mentorId && !under.hasAttribute("aria-disabled")) onTarget(mentorId);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  const proposal = selected && target ? { student: studentName(selected), mentor: mentorName(target) } : null;
  const existing = selected && target ? requestsFor(selected, target) : null;
  const alreadyPaired = selected && target ? pairedWith(selected, target) : null;

  return (
    <div className="e-grid">
      <Card variant="plain" span="full">
        <CardBody>
          {/* No visible labels, as on Enrollment (Thomas, Sep 18 2026): the
              placeholder and each select's first option say what the control
              is, so the names are for a screen reader only. */}
          <div className="e-roster-filters">
            <Input
              type="search"
              aria-label="Find a student"
              value={query}
              placeholder="Start typing a first name"
              onChange={(e) => onQuery(e.target.value)}
              autoComplete="off"
            />
            <div className="e-roster-filters-pair">
              <Select
                aria-label="Filter students by mentorship"
                value={studentFilter}
                onChange={(e) => onStudentFilter(e.target.value as "all" | "without")}
              >
                <option value="all">All students</option>
                <option value="without">Without a mentor</option>
              </Select>
              {activeCohorts.length > 0 ? (
                <Select aria-label="Filter students by cohort" value={cohortFilter} onChange={(e) => onCohortFilter(e.target.value)}>
                  <option value="">All cohorts</option>
                  {activeCohorts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card span="full">
        <CardHeader>
          {/* Named for the act, not the furniture (Thomas, Sep 17 2026): "The
              board" read as the governing kind on a screen an institution's
              own officers use. It pairs with the card under it, "Requests your
              team sent", and it is the Worker's own word for what the call
              does. */}
          <CardTitle sub={`${formatCount(students.length)}${sampleOf !== null && students.length === roster.students.length ? ` of ${formatCount(sampleOf)}` : ""} ${students.length === 1 && sampleOf === null ? "student" : "students"} · ${formatCount(mentors.length)} ${mentors.length === 1 ? "mentor" : "mentors"} on your institution`}>
            Ask for a mentorship
          </CardTitle>
        </CardHeader>
        <CardBody>
          {!matches.available ? (
            <StateCard variant="not-measured" title="Matching is not available here yet">
              {matches.reason ?? "Requests cannot be sent from this deployment."}
            </StateCard>
          ) : null}

          <ul className="e-rope-legend" aria-label="What the ropes mean">
            <li><i className="is-pending" /> Confirming match</li>
            <li><i className="is-active" /> Mentoring</li>
            <li><i className="is-inactive" /> Ended</li>
            <li><i className="is-existing" /> Already paired in the app</li>
          </ul>

          {proposal ? (
            <div className="e-rope-confirm" role="status">
              <span className="e-ui">
                {alreadyPaired
                  ? alreadyPaired === "ACTIVE"
                    ? `${proposal.mentor} already mentors ${proposal.student}.`
                    : `${proposal.student} has already asked ${proposal.mentor} in the app, and the mentor has not answered yet.`
                  : existing && existing.status !== "INACTIVE"
                    ? `${proposal.mentor} was already asked for ${proposal.student}: ${STATUS_LABEL[existing.status].toLowerCase()}.`
                    : `Ask ${proposal.mentor} to mentor ${proposal.student}? The mentor decides in the app.`}
              </span>
              {alreadyPaired || (existing && existing.status !== "INACTIVE") ? null : (
                <Button variant="primary" size="sm" disabled={busy || !matches.available} onClick={() => void onSend(selected!, target!, proposal.student, proposal.mentor)}>
                  {busy ? "Sending…" : "Send request"}
                </Button>
              )}
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => onTarget(null)}>
                Cancel
              </Button>
            </div>
          ) : selected ? (
            <p className="e-note" style={{ marginBottom: "var(--sp-4)" }}>
              {studentName(selected)} selected. Choose a mentor on the right, or pull the rope to one.
            </p>
          ) : null}
          {notice ? (
            <p
              className={notice.tone === "error" ? "e-field-error" : "e-note"}
              role={notice.tone === "error" ? "alert" : "status"}
              style={{ marginBottom: "var(--sp-4)" }}
            >
              {notice.text}
            </p>
          ) : null}

          {mentors.length === 0 ? (
            <StateCard variant="empty" title="No mentor is affiliated yet">
              A mentor joins your institution from your join link, or names it on their P3 profile. Mentors appear here the hour after.
            </StateCard>
          ) : students.length === 0 ? (
            <StateCard variant="empty" title={roster.students.length === 0 ? "No student has joined yet" : "No student matches"}>
              {roster.students.length === 0 ? "Students appear here the hour after they join from your link, code or poster." : "Try fewer letters, or clear the filters."}
            </StateCard>
          ) : (
            <div className="e-rope-board" ref={boardRef}>
              <div className="e-rope-col" aria-label="Students">
                <p className="e-label e-rope-col-head">Students</p>
                {students.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    ref={setRow(`s:${s.id}`)}
                    className={`e-rope-row${selected === s.id ? " is-selected" : ""}`}
                    aria-pressed={selected === s.id}
                    disabled={busy}
                    onClick={() => onSelect(selected === s.id ? null : s.id)}
                    onPointerDown={startDrag(s.id)}
                  >
                    <span className="e-rope-row-text">
                      <span className="e-ui">{s.name}</span>
                      <span className="e-note">
                        {pendingFrom(s.id) > 0 ? `${formatCount(pendingFrom(s.id))} ${pendingFrom(s.id) === 1 ? "request" : "requests"} waiting` : `Joined ${s.joinedAt ? formatDate(s.joinedAt * 1000) : "unknown"}`}
                      </span>
                    </span>
                    <span className="e-rope-row-side">
                      {studentChip(s)}
                      <span className="e-rope-knot" aria-hidden="true" />
                    </span>
                  </button>
                ))}
              </div>
              <div className="e-rope-lane" aria-hidden="true" />
              <div className="e-rope-col" aria-label="Mentors">
                <p className="e-label e-rope-col-head">Mentors</p>
                {mentors.map((m) => {
                  const full = mentorFull(m);
                  const approved = (m.status ?? "").toUpperCase() === "APPROVED";
                  // The pair already exists, whoever made it (Sep 17 2026):
                  // the rope is already drawn and asking again is refused by
                  // the platform, so the row does not offer it.
                  const paired = selected ? pairedWith(selected, m.id) : null;
                  const disabled = busy || !selected || full || !approved || paired !== null;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      ref={setRow(`m:${m.id}`)}
                      data-mentor-id={m.id}
                      className={`e-rope-row${target === m.id || hovered === m.id ? " is-target" : ""}${full || paired !== null ? " is-full" : ""}`}
                      aria-disabled={full || !approved || paired !== null ? true : undefined}
                      disabled={disabled}
                      onClick={() => {
                        if (selected) onTarget(m.id);
                      }}
                    >
                      <span className="e-rope-row-side">
                        <span className="e-rope-knot" aria-hidden="true" />
                      </span>
                      <span className="e-rope-row-text" style={{ flex: 1 }}>
                        <span className="e-ui">{m.name}</span>
                        <span className="e-note">{mentorLine(m)}</span>
                      </span>
                      <span className="e-rope-row-side">
                        {paired !== null ? (
                          <Chip tone="good">{paired === "ACTIVE" ? "Already mentoring" : "Already asked"}</Chip>
                        ) : !approved ? (
                          <Chip tone="neutral">Not approved yet</Chip>
                        ) : full ? (
                          <Chip tone="neutral">Full</Chip>
                        ) : null}
                        {selected && suggested.includes(m.id) && !full && approved && paired === null ? <Chip tone="accent">Suggested</Chip> : null}
                        {pendingTo(m.id) > 0 ? <Chip tone="warn">{formatCount(pendingTo(m.id))} asked</Chip> : null}
                      </span>
                    </button>
                  );
                })}
              </div>
              <svg className="e-rope-svg" aria-hidden="true">
                {ropes.map((rope) => {
                  const tone = rope.status.toLowerCase();
                  return (
                    <g key={rope.key}>
                      <path className={`e-rope is-${tone}`} d={ropePath(rope.from, rope.to)}>
                        <title>{rope.title}</title>
                      </path>
                      <circle className={`e-rope-end is-${tone}`} cx={rope.from.x} cy={rope.from.y} r={4} />
                      {rope.status === "ACTIVE" ? (
                        <circle className="e-rope-mark" cx={(rope.from.x + rope.to.x) / 2} cy={(rope.from.y + rope.to.y) / 2} r={5} />
                      ) : null}
                      {rope.status !== "drawing" ? <circle className={`e-rope-end is-${tone}`} cx={rope.to.x} cy={rope.to.y} r={4} /> : null}
                    </g>
                  );
                })}
              </svg>
            </div>
          )}
        </CardBody>
        {/* Footnote text sits in an e-note, as Enrollment's and Billing's do
            (Sep 18 2026): bare text in the slot rendered at the body size in
            the body color rather than the meta size in the muted grey. */}
        <CardFootnote>
          <p className="e-note">{MATCHING_NOTE}</p>
        </CardFootnote>
      </Card>

      <Card variant="table" span="full">
        <CardHeader>
          <CardTitle>Requests your team sent</CardTitle>
        </CardHeader>
        <CardBody>
          {matches.requests.length === 0 ? (
            <StateCard variant="empty" title="No request yet">
              Pull a rope from a student to a mentor above. Each request is listed here with the mentor's answer.
            </StateCard>
          ) : (
            <DataTable
              caption="Match requests this institution sent"
              rows={matches.requests}
              rowKey={(r) => r.id}
              cap={500}
              moreNoun="request"
              stackTitle={(r) => `${studentName(r.studentId)} and ${mentorName(r.mentorId)}`}
              columns={[
                { key: "student", header: "Student", cell: (r) => <span className="e-ui">{studentName(r.studentId)}</span>, text: (r) => studentName(r.studentId), align: "left" },
                { key: "mentor", header: "Mentor", cell: (r) => <span className="e-ui">{mentorName(r.mentorId)}</span>, text: (r) => mentorName(r.mentorId), align: "left" },
                {
                  key: "status",
                  header: "Status",
                  // A withdrawn row is INACTIVE like a declined one, and the
                  // chip says which (Sep 18 2026): the two are the same
                  // state on the platform and different acts in this room.
                  cell: (r: MatchRequestWire) =>
                    r.status === "ACTIVE" ? <Chip tone="good">{STATUS_LABEL.ACTIVE}</Chip> : r.status === "PENDING" ? <Chip tone="warn">{STATUS_LABEL.PENDING}</Chip> : <Chip tone="neutral">{statusLabel(r)}</Chip>,
                  text: (r) => statusLabel(r),
                  align: "left",
                },
                { key: "sent", header: "Sent", cell: (r) => <span style={{ whiteSpace: "nowrap" }}>{formatDate(r.requestedAt * 1000)}</span>, text: (r) => formatDate(r.requestedAt * 1000), align: "left" },
                { key: "checked", header: "Checked", cell: (r) => <span style={{ whiteSpace: "nowrap" }}>{formatDate(r.checkedAt * 1000)}</span>, text: (r) => formatDate(r.checkedAt * 1000), align: "left", secondary: true },
                {
                  key: "actions",
                  header: "",
                  // Only a request still waiting on the mentor can be taken
                  // back, and only by an administrator (Sep 18 2026); the
                  // Worker holds both rules, and the button follows them.
                  cell: (r: MatchRequestWire) => {
                    const refusal = withdrawRefusal?.requestId === r.id ? withdrawRefusal.text : null;
                    const offered = r.status === "PENDING" && canWithdraw;
                    if (!offered && refusal === null) return null;
                    return (
                      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "var(--sp-1)" }}>
                        {offered ? (
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void onWithdraw(r, mentorName(r.mentorId))}>
                            {withdrawing === r.id ? "Withdrawing…" : "Withdraw"}
                          </Button>
                        ) : null}
                        {refusal !== null ? (
                          <span className="e-field-error" role="alert" style={{ maxWidth: "22rem", textAlign: "right" }}>
                            {refusal}
                          </span>
                        ) : null}
                      </span>
                    );
                  },
                  text: () => "",
                  align: "right",
                },
              ]}
            />
          )}
        </CardBody>
        <CardFootnote>
          <p className="e-note">
            Statuses are read from the platform on the hourly refresh. A request the mentor declines shows as ended.
            {/* Only while withdrawing is on (Sep 19 2026): the sentence described a button nobody can see. */}
            {WITHDRAW_LIVE ? " A request your team withdraws shows as withdrawn, and the pair can be asked again." : null}
          </p>
        </CardFootnote>
      </Card>
    </div>
  );
}
